import { Copy } from 'lucide-react';
import { useToast } from '@/shared/hooks/useToast';
import { Alert } from '@/shared/ui/Alert/Alert';
import { Button } from '@/shared/ui/Button/Button';
import { Modal } from '@/shared/ui/Modal/Modal';
import { Spinner } from '@/shared/ui/Spinner/Spinner';
import { Tooltip } from '@/shared/ui/Tooltip/Tooltip';
import { useVersaoApiQuery } from './useVersaoApiQuery';
import {
  commitCurto,
  formatarInstante,
  obterTela,
  obterVersaoWeb,
  textoParaCopiar,
} from './versaoSobre';
import styles from './SobreModal.module.css';

const PRODUTO = 'MomyEdu Escola (web)';

type Props = {
  open: boolean;
  onClose: () => void;
};

function Commit({ sha }: { sha: string | null }) {
  if (!sha) return <span>—</span>;
  return (
    <Tooltip content={sha} placement="top">
      <code className={styles.commit}>{commitCurto(sha)}</code>
    </Tooltip>
  );
}

export function SobreModal({ open, onClose }: Props) {
  const toast = useToast();
  const web = obterVersaoWeb();
  const tela = obterTela();
  const api = useVersaoApiQuery(open);

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(textoParaCopiar(PRODUTO, web, api.data, tela));
      toast.success('Informações copiadas.');
    } catch {
      toast.error('Não foi possível copiar.');
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Sobre"
      footer={
        <Button variant="outline" onClick={() => void copiar()}>
          <Copy size={16} aria-hidden />
          Copiar informações
        </Button>
      }
    >
      <section className={styles.bloco} aria-labelledby="sobre-web">
        <h3 id="sobre-web" className={styles.titulo}>
          {PRODUTO}
        </h3>
        <dl className={styles.lista}>
          <dt>Versão</dt>
          <dd>{web.versao || '—'}</dd>
          <dt>Commit</dt>
          <dd>
            <Commit sha={web.commit || null} />
          </dd>
          <dt>Build</dt>
          <dd>{formatarInstante(web.buildEm)}</dd>
        </dl>
      </section>

      <section className={styles.bloco} aria-labelledby="sobre-dispositivo">
        <h3 id="sobre-dispositivo" className={styles.titulo}>
          Seu dispositivo
        </h3>
        <dl className={styles.lista}>
          <dt>Tela</dt>
          <dd>{tela.tela}</dd>
          <dt>Janela</dt>
          <dd>{tela.janela}</dd>
          <dt>Escala</dt>
          <dd>{tela.escala}</dd>
        </dl>
      </section>

      <section className={styles.bloco} aria-labelledby="sobre-api">
        <h3 id="sobre-api" className={styles.titulo}>
          API
        </h3>
        {api.isPending ? (
          <Spinner message="Consultando a API…" />
        ) : api.isError || !api.data ? (
          <Alert variant="error">Não foi possível consultar a versão da API.</Alert>
        ) : (
          <dl className={styles.lista}>
            <dt>Versão</dt>
            <dd>{api.data.versao ?? '—'}</dd>
            <dt>Commit</dt>
            <dd>
              <Commit sha={api.data.commit} />
            </dd>
            <dt>Ambiente</dt>
            <dd>{api.data.perfis.join(', ') || '—'}</dd>
            <dt>Build</dt>
            <dd>{formatarInstante(api.data.buildEm)}</dd>
            <dt>No ar desde</dt>
            <dd>{formatarInstante(api.data.iniciadaEm)}</dd>
          </dl>
        )}
      </section>
    </Modal>
  );
}
